import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, ArrowRight, MapPin, Compass } from "lucide-react";
import { api, mediaSrc } from "@/lib/api";
import { SourceBadge } from "@/components/SourceBadge";

const TYPE_LABEL = { outfitter: "Outfitter", guide: "Guide" };

// Contextual, clearly-labeled HomeRange Partner recommendations (outfitters/guides + gear)
// for state pages and hunt records. Real published data only — renders nothing when empty.
export function PartnerRecommendations({ state, species, theme = "light", className = "" }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.recommendations({ state, species, limit: 4 })
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(false); });
    return () => { alive = false; };
  }, [state, species]);

  if (!data || (!data.outfitters?.length && !data.gear?.length)) return null;

  const dark = theme === "dark";
  const cardBg = dark ? "bg-white/[0.04] border-white/10 hover:border-blaze/50" : "bg-white border-saddle/15 hover:border-blaze/40";
  const title = dark ? "text-bone" : "text-charcoal";
  const sub = dark ? "text-bone/55" : "text-charcoal/55";
  const scope = species && state ? `${species} · ${state}` : state || species || "your hunt";

  return (
    <section className={className} data-testid="partner-recommendations">
      <div className="flex items-center gap-3 flex-wrap mb-1">
        <h3 className={`font-serif text-2xl sm:text-3xl tracking-tight ${title}`}>Recommended for {scope}</h3>
        <SourceBadge tier="partner" />
      </div>
      <p className={`text-sm ${sub} mb-5`}>Verified HomeRange partners — promotional listings, not official regulation sources.</p>

      {data.outfitters?.length > 0 && (
        <div className="mb-6">
          <p className={`hr-overline text-[11px] mb-3 ${dark ? "text-bone/45" : "text-charcoal/45"}`}>Outfitters &amp; Guides</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.outfitters.map((p) => (
              <Link key={p.id} to={p.public_path} className={`group flex items-center gap-3 border rounded-xl p-3.5 transition-colors ${cardBg}`} data-testid={`rec-outfitter-${p.id}`}>
                <div className="w-12 h-12 rounded-lg bg-earth/15 overflow-hidden grid place-items-center shrink-0">
                  {p.logo ? <img src={mediaSrc(p.logo)} alt={p.name} className="w-full h-full object-cover" /> : <Compass size={18} className="text-earth" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className={`font-semibold truncate ${title}`}>{p.name}</p>
                    {(p.verification_status || "").startsWith("verified") && <BadgeCheck size={14} className="text-blaze shrink-0" />}
                  </div>
                  <p className={`text-xs truncate flex items-center gap-1 ${sub}`}>
                    <span className="uppercase tracking-wide font-semibold text-blaze/80">{TYPE_LABEL[p.partner_type] || "Partner"}</span>
                    {p.location && <><MapPin size={10} /> {p.location}</>}
                  </p>
                </div>
                <ArrowRight size={16} className={`${dark ? "text-bone/40" : "text-charcoal/30"} group-hover:text-blaze group-hover:translate-x-1 transition-all shrink-0`} />
              </Link>
            ))}
          </div>
        </div>
      )}

      {data.gear?.length > 0 && (
        <div>
          <p className={`hr-overline text-[11px] mb-3 ${dark ? "text-bone/45" : "text-charcoal/45"}`}>Gear from partners</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {data.gear.map((g) => (
              <Link key={g.id} to={`/store/${g.business_id}`} className={`group border rounded-xl overflow-hidden transition-colors ${cardBg}`} data-testid={`rec-gear-${g.id}`}>
                <div className="aspect-square bg-earth/10 overflow-hidden">
                  {g.image ? <img src={mediaSrc(g.image)} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full grid place-items-center text-earth/40"><Compass size={22} /></div>}
                </div>
                <div className="p-3">
                  <p className={`text-[13px] font-semibold leading-tight line-clamp-2 ${title}`}>{g.title}</p>
                  <p className={`text-xs mt-1 ${sub}`}>{g.business_name}</p>
                  {typeof g.price === "number" && <p className="text-blaze font-bold text-sm mt-1">${g.price.toFixed(2)}</p>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
